import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { allSigned, type BookingTask } from './bookingTasks';

const callPack = httpsCallable<any, any>(functions, 'buyerFormPack');
const errText = (e: any) => String(e?.message || 'Something went wrong.').replace(/^Firebase(Error)?:\s*/i, '');
const inputStyle: React.CSSProperties = { font: 'inherit', fontSize: 14, padding: '9px 11px', borderRadius: 11, border: '1px solid var(--ot-line)', background: 'var(--ot-surface)', color: 'var(--ot-ink)', width: '100%' };

type Buyer = { id: string; name: string };
type SignDoc = 'bookingForm' | 'allotment';

/** Rows at the top of Pending work: review, create, then print and sign with a tick for each buyer. */
export function BookingFormTasks({ place, tasks, buyers, bookingFormUrl, allotmentUrl, signing, onReview, onCreate, onUploadBookingForm, onUploadAllotment, onTick }: {
  place: { projectId: string; unitId: string; bookingId: string };
  tasks: BookingTask[];
  buyers: Buyer[];
  bookingFormUrl: string | null;
  allotmentUrl: string | null;
  signing: Partial<Record<SignDoc, Record<string, boolean>>>;
  onReview: () => void;
  onCreate: () => void;
  onUploadBookingForm: () => void;
  onUploadAllotment: () => void;
  onTick: (doc: SignDoc, buyerId: string, on: boolean) => void;
}) {
  const [ticks, setTicks] = useState(signing);
  const [location, setLocation] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [pack, setPack] = useState<{ url: string; size: number; problems: string[]; tooLarge: boolean } | null>(null);
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState('');
  const run = async (action: string, extra: Record<string, unknown>, after: (data: any) => void) => {
    setBusy(action);
    setError('');
    try {
      const res = await callPack({ ...place, action, ...extra });
      after(res.data);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy('');
    }
  };
  const ids = buyers.map((b) => b.id);
  const toggle = (doc: SignDoc, id: string, on: boolean) => {
    setTicks((prev) => ({ ...prev, [doc]: { ...(prev[doc] || {}), [id]: on } }));
    onTick(doc, id, on);
  };
  const signRow = (doc: SignDoc, title: string, url: string, onUpload: () => void) => (
    <div className="ou-sign" key={doc}>
      <div className="ou-sign-h"><b>{title}</b><a className="ot-btn sm" href={url} target="_blank" rel="noopener noreferrer">Print</a></div>
      <div className="ou-sign-names" role="group" aria-label={`Signed ${title}`}>
        {buyers.map((b) => (
          <label key={b.id}><input type="checkbox" checked={!!ticks[doc]?.[b.id]} onChange={(e) => toggle(doc, b.id, e.target.checked)} />{b.name}</label>
        ))}
      </div>
      <button className="ot-btn sm dark" disabled={!allSigned(ticks[doc], ids)} onClick={onUpload} title={allSigned(ticks[doc], ids) ? undefined : 'Tick every buyer once they have signed'}>Upload signed copy</button>
    </div>
  );
  return (
    <div className="ou-pw-group">
      {tasks.includes('review') ? (
        <div className="ot-list"><div className="ot-item"><span className="bd"><span className="t">Review buyer details</span><span className="s" style={{ display: 'block' }}>The buyer sent the form</span></span><button className="ot-btn sm dark" onClick={onReview}>Review</button></div></div>
      ) : null}
      {tasks.includes('create') ? (
        <div className="ot-list"><div className="ot-item"><span className="bd"><span className="t">Create booking form</span><span className="s" style={{ display: 'block' }}>Buyer details are approved</span></span><button className="ot-btn sm dark" onClick={onCreate}>Create</button></div></div>
      ) : null}
      {tasks.includes('sign') && bookingFormUrl ? (
        <>
          <div className="ot-lbl ou-pw-lbl">Print and sign</div>
          {signRow('bookingForm', 'Booking form', bookingFormUrl, onUploadBookingForm)}
          {allotmentUrl ? signRow('allotment', 'Allotment letter', allotmentUrl, onUploadAllotment) : null}
        </>
      ) : null}
      {tasks.includes('digitalCopy') ? (
        <div className="ou-sign">
          <b>Keep the signed original safe</b>
          <label className="muted" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
            Where is the original kept?
            <input style={inputStyle} placeholder="For example: Office, Box 12" value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>
          <button className="ot-btn sm dark" disabled={!location.trim() || !!busy} onClick={() => run('digitalCopy', { fileLocation: location }, () => undefined)}>{busy === 'digitalCopy' ? 'Creating…' : 'Create digital copy'}</button>
        </div>
      ) : null}
      {tasks.includes('advocate') && !sentTo ? (
        <div className="ou-sign">
          <b>Send to advocate</b>
          <span className="faint" style={{ fontSize: 12.5 }}>One PDF: buyer details, digital copy, signed form and every ID, with full ID numbers.</span>
          {pack ? (
            <>
              <a className="ot-btn sm" href={pack.url} target="_blank" rel="noopener noreferrer">Open PDF ({(pack.size / (1024 * 1024)).toFixed(1)} MB)</a>
              {pack.problems.length ? <ul role="alert" style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--ot-bad-ink)' }}>{pack.problems.map((p) => <li key={p}>{p}</li>)}</ul> : null}
              {pack.tooLarge ? <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--ot-bad-ink)' }}>This PDF is too large to email. Replace the largest ID copies and create it again.</p> : (
                <>
                  <label className="muted" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                    Advocate's email
                    <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </label>
                  <button className="ot-btn sm dark" disabled={!email.trim() || !!busy} onClick={() => run('advocateSend', { email }, (d) => setSentTo(String(d.to || email)))}>{busy === 'advocateSend' ? 'Sending…' : pack.problems.length ? 'Send anyway' : 'Send'}</button>
                </>
              )}
            </>
          ) : null}
          <button className="ot-btn sm" disabled={!!busy} onClick={() => run('advocatePdf', {}, (d) => { setPack(d); setEmail(d.advocateEmail || ''); })}>{busy === 'advocatePdf' ? 'Creating, about a minute…' : pack ? 'Create again' : 'Create advocate PDF'}</button>
        </div>
      ) : null}
      {sentTo ? <p role="status" style={{ margin: 0, fontSize: 13 }}>Sent to {sentTo}.</p> : null}
      {error ? <p role="alert" style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--ot-bad-ink)' }}>{error}</p> : null}
    </div>
  );
}
